import { timeFormatLocale } from 'd3-time-format';
import { PRESSURE_UNIT, RAIN_UNIT, SPEED_UNIT } from './constants';
import { getMonthNames } from './date-utils';
import {
	convertPressure,
	convertRainfall,
	convertTemperature,
	convertTemperatureTrend,
	convertWindSpeed
} from './unit-converters';

/**
 * Take a temperature value and unit, apply any necessary unit conversions and return a formatted value.
 *
 * @param value - temperature value
 * @param unit - see TEMPERATURE_UNIT
 * @param alreadyConverted - the value is already converted (default false)
 * @returns {string}
 */
export function formatTemperature(value, unit, alreadyConverted = false) {
	const convertedValue = alreadyConverted ? value : convertTemperature(value, unit);
	return convertedValue.toFixed(1);
}

/**
 * Take a temperature interval value and unit, apply any necessary unit conversions and return a formatted value.
 *
 * @param value - temperature value
 * @param unit - see TEMPERATURE_UNIT
 * @param alreadyConverted - the value is already converted (default false)
 * @returns {string}
 */
export function formatTemperatureInterval(value, unit, alreadyConverted = false) {
	const convertedValue = alreadyConverted ? value : convertTemperatureTrend(value, unit);
	return convertedValue.toFixed(1);
}

/**
 * Take a rainfall value and unit, apply any necessary unit conversions and return a formatted value.
 *
 * @param value - rainfall value
 * @param unit - see RAIN_UNIT
 * @param alreadyConverted - the value is already converted (default false)
 * @returns {string}
 */
export function formatRainfall(value, unit, alreadyConverted = false) {
	const convertedValue = alreadyConverted ? value : convertRainfall(value, unit);
	const dp = unit === RAIN_UNIT.IN ? 2 : 1;
	return convertedValue.toFixed(dp);
}

/**
 * Take a rain rate value and unit, apply any necessary unit conversions and return a formatted value.
 *
 * @param value - rain rate value
 * @param unit - see RAIN_UNIT
 * @param morePreciseWhenLow - return a more precise value when the value is lower than a certain threshold
 * @param alreadyConverted - the value is already converted (default false)
 * @returns {string}
 */
export function formatRainRate(value, unit, morePreciseWhenLow = true, alreadyConverted = false) {
	// get the converted value
	const convertedValue = alreadyConverted ? value : convertRainfall(value, unit);

	// calculate the threshold value to compare against at which precision decreases
	const thresholdMm = unit === RAIN_UNIT.IN ? 50 / 25.4 : 50;

	// use higher precision if the `morePreciseWhenLow` flag is true and the value is below the threshold
	const useHigherPrecision = morePreciseWhenLow && convertedValue < thresholdMm;
	const decimalPlacesAdjustment = useHigherPrecision ? 1 : 0;

	const dp = (unit === RAIN_UNIT.IN ? 1 : 0) + decimalPlacesAdjustment;
	return convertedValue.toFixed(dp);
}

/**
 * Take a pressure value and unit, apply any necessary unit conversions and return a formatted value.
 *
 * @param value - pressure value
 * @param unit - see PRESSURE_UNIT
 * @param alreadyConverted - the value is already converted (default false)
 * @returns {string}
 */
export function formatPressure(value, unit, alreadyConverted = false) {
	const convertedValue = alreadyConverted ? value : convertPressure(value, unit);
	const dp = unit === PRESSURE_UNIT.INHG ? 2 : 1;
	return convertedValue.toFixed(dp);
}

/**
 * Take a wind speed value and unit, apply any necessary speed unit conversions
 * and return a formatted value.
 *
 * @param value - wind speed value
 * @param unit - see SPEED_UNIT
 * @param alreadyConverted - the value is already converted (default false)
 * @returns {string}
 */
export function formatWindSpeed(value, unit, alreadyConverted = false) {
	const convertedValue = alreadyConverted ? value : convertWindSpeed(value, unit);
	return convertedValue.toFixed(1);
}

/**
 * Take a wind gust value and unit, apply any necessary speed unit conversions
 * and return a formatted value.
 *
 * @param value - wind gust value
 * @param unit - see SPEED_UNIT
 * @param alreadyConverted - the value is already converted (default false)
 * @param roundHighMs - if the unit is m/s and the value is in double digits, should it be rounded to the nearest whole number? (default false)
 * @returns {string | number}
 */
export function formatWindGust(value, unit, alreadyConverted = false, roundHighMs = false) {
	const convertedValue = alreadyConverted ? value : convertWindSpeed(value, unit);

	if (unit !== SPEED_UNIT.MS || (roundHighMs && convertedValue >= 10)) {
		return Math.round(convertedValue);
	}

	return convertedValue.toFixed(1);
}

/**
 * Converts a wind speed unit to a wind run unit.
 *
 * @param speedUnit - see SPEED_UNIT
 * @returns {string}
 */
export function convertSpeedUnitToWindRunUnit(speedUnit) {
	switch (speedUnit) {
		case SPEED_UNIT.KMH:
			return 'km';
		case SPEED_UNIT.MPH:
			return 'miles';
		case SPEED_UNIT.KNOTS:
			return 'nautical miles';
		case SPEED_UNIT.MS:
			return 'km';
		default:
			return '';
	}
}

/**
 * Custom D3 date/time locale
 */
export const customLocale = timeFormatLocale({
	dateTime: '%-I:%M:%S %p %A %-d %B %Y',
	date: '%A %-d %B %Y',
	time: '%-I:%M %p',
	periods: ['am', 'pm'],
	days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
	shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
	months: getMonthNames(),
	shortMonths: getMonthNames().map(month => month.slice(0, 3))
});

/**
 * Return a function which converts from one datetime format to another.
 *
 * @param fromFormat - see D3 datetime format docs
 * @param toFormat - see D3 datetime format docs
 * @returns {function(string): string}
 */
export function getDateTimeFormatConverter(fromFormat, toFormat) {
	const parser = customLocale.parse(fromFormat);
	const formatter = customLocale.format(toFormat);
	return t => formatter(parser(t));
}



// WEBPACK FOOTER //
// ./utils/formatters.js